import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Card, CardContent, CardHeader, Chip, Grid, IconButton, LinearProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography, } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { NavLink, useParams } from "react-router-dom";
import { PRIMARY_COLOR } from "../util/constants";
import { handleResponse } from "../util/integration";
import { fetchAuthorInvoicesAPI, fetchBillingStatusesAPI, fetchOperatorInvoicesAPI, fetchPurchaserInvoicesAPI, } from "../api/invoicing_api";
const INVOICE_TYPE = Object.freeze({
    product: "PRODUCT",
    transaction: "TRANSACTION",
});
const INVOICE_STATUS = Object.freeze({
    issued: "ISSUED",
    cancelled: "CANCELLED",
});
const DOCUMENT_TYPE = Object.freeze({
    invoice: "INVOICE",
    cancellation: "CANCELLATION",
});
const LABEL = Object.freeze({
    invoices: "Rechnungen",
    transactionInvoices: "Rechnungen (Transaktion)",
    productInvoices: "Rechnungen (Produkt)",
    date: "Datum",
    document: "Dokument",
    invoiceNumber: "Rechnungsnummer",
    amount: "Betrag",
    actions: "Aktionen",
    invoice: "Rechnung",
    cancellationInvoice: "Stornorechnung",
    openLead: "Lead öffnen",
    noBillingData: "Keine Abrechnungsdaten",
    noBillingStatus: "Für diesen Lead wurde kein Abrechnungsstatus gefunden.",
    noInvoices: "Keine Rechnungen vorhanden",
    noInvoicesForArea: "Für diesen Bereich wurden noch keine Rechnungen erstellt.",
    rowsPerPage: "Zeilen pro Seite:",
    invoiceDocuments: "Rechnungsdokumente",
    lead: "Lead",
    download: "herunterladen",
});
const MESSAGE = Object.freeze({
    downloadFailed: "Die Rechnung konnte nicht heruntergeladen werden.",
    unknownPdfFormat: "Das Rechnungs-PDF besitzt ein unbekanntes Format.",
});
const CONFIG = Object.freeze({
    rowsPerPageOptions: Object.freeze([5, 10, 25]),
    defaultPage: 0,
    defaultRowsPerPage: 10,
    timestampMillisecondsThreshold: 1_000_000_000_000,
    currencyLocale: "de-DE",
    currencyCode: "EUR",
    dateLocale: "de-DE",
    objectUrlRevokeDelay: 0,
    integerRadix: 10,
});
const VALUE = Object.freeze({
    empty: "",
    fallback: "–",
    unknownInvoiceType: "UNKNOWN",
    invoiceKeyPrefix: "invoice-",
    invoiceKeySuffix: "invoice",
    cancellationKeySuffix: "cancellation",
    pdfExtension: ".pdf",
    pdfMimeType: "application/pdf",
    base64Separator: ",",
    downloadElement: "a",
    leadRoutePrefix: "/leads/",
    keySeparator: "-",
});
const UI = Object.freeze({
    boolean: Object.freeze({
        true: true,
    }),
    component: Object.freeze({
        div: "div",
        span: "span",
    }),
    variant: Object.freeze({
        outlined: "outlined",
        overline: "overline",
        h6: "h6",
        body1: "body1",
        body2: "body2",
    }),
    size: Object.freeze({
        small: "small",
    }),
    color: Object.freeze({
        secondaryText: "text.secondary",
        warning: "warning",
        default: "default",
    }),
    align: Object.freeze({
        start: "flex-start",
        center: "center",
        right: "right",
    }),
    direction: Object.freeze({
        row: "row",
    }),
    grid: Object.freeze({
        full: 12,
        sectionSpacing: 2.5,
    }),
    stack: Object.freeze({
        titleSpacing: 1.25,
    }),
});
const STYLE = Object.freeze({
    wrapper: {
        mt: 6,
    },
    statusCard: {
        borderRadius: 3,
        borderColor: "divider",
    },
    statusContent: {
        py: 5,
        textAlign: "center",
    },
    statusIcon: {
        mb: 1,
        fontSize: 40,
        color: "text.disabled",
    },
    statusTitle: {
        fontWeight: 500,
    },
    statusDescription: {
        mt: 0.5,
    },
    invoicingCard: {
        overflow: "hidden",
        borderRadius: 3,
        borderColor: "divider",
        boxShadow: "0 8px 30px rgba(15, 23, 42, 0.07)",
    },
    invoicingContent: {
        p: {
            xs: 2,
            md: 2.5,
        },
        backgroundColor: "grey.50",
    },
    headerTitleContainer: {
        minWidth: 0,
    },
    headerEyebrow: {
        display: "block",
        lineHeight: 1.3,
        letterSpacing: "0.09em",
        opacity: 0.7,
    },
    headerTitle: {
        mt: 0.25,
        fontWeight: 500,
    },
    headerAction: {
        flexShrink: 0,
        color: "common.white",
        border: 1,
        borderColor: "rgba(255, 255, 255, 0.28)",
        "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.12)",
        },
    },
    detailsProgress: {
        mb: 2,
    },
    invoiceCard: {
        width: "100%",
        height: "fit-content",
        overflow: "hidden",
        borderRadius: 2.5,
        borderColor: "divider",
        backgroundColor: "common.white",
        boxShadow: "0 4px 18px rgba(15, 23, 42, 0.055)",
    },
    cardHeader: {
        px: 2.5,
        py: 2,
        borderBottom: 1,
        borderColor: "divider",
    },
    cardTitleStack: {
        minWidth: 0,
    },
    cardTitle: {
        minWidth: 0,
        fontSize: "1.1rem",
        fontWeight: 500,
        letterSpacing: "-0.01em",
    },
    countChip: {
        height: 24,
        fontWeight: 500,
    },
    cardContent: {
        p: 0,
        "&:last-child": {
            pb: 0,
        },
    },
    emptyState: {
        px: 3,
        py: 5,
        textAlign: "center",
        color: "text.secondary",
    },
    emptyStateIcon: {
        mb: 1,
        fontSize: 38,
        color: "text.disabled",
    },
    emptyStateTitle: {
        fontWeight: 500,
        color: "text.primary",
    },
    emptyStateDescription: {
        mt: 0.5,
    },
    tableContainer: {
        overflowX: "auto",
    },
    table: {
        minWidth: 760,
    },
    tableHeadRow: {
        backgroundColor: "grey.50",
        "& th": {
            py: 1.5,
            fontSize: "0.78rem",
            fontWeight: 500,
            letterSpacing: "0.01em",
            color: "text.secondary",
            whiteSpace: "nowrap",
        },
    },
    tableRow: {
        "&:last-child td": {
            borderBottom: 0,
        },
    },
    paginatedTableRow: {
        "&:last-child td": {
            borderBottom: undefined,
        },
    },
    dateCell: {
        py: 1.55,
        fontSize: "0.875rem",
        lineHeight: 1.45,
        whiteSpace: "nowrap",
    },
    documentCell: {
        py: 1.1,
        whiteSpace: "nowrap",
    },
    numberCell: {
        py: 1.55,
        fontSize: "0.875rem",
        lineHeight: 1.45,
        whiteSpace: "nowrap",
        fontWeight: 500,
        fontVariantNumeric: "tabular-nums",
    },
    actionCell: {
        py: 1,
    },
    documentChip: {
        minWidth: 112,
        fontWeight: 500,
    },
    downloadButton: {
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        "&:hover": {
            borderColor: "primary.main",
            color: "primary.main",
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
        },
    },
    pagination: {
        borderTop: 1,
        borderColor: "divider",
        "& .MuiTablePagination-toolbar": {
            minHeight: 56,
        },
    },
});
const createColorStyles = (primaryColor) => Object.freeze({
    invoicingHeader: {
        px: {
            xs: 2,
            sm: 3,
        },
        py: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        backgroundColor: primaryColor,
        color: "common.white",
    },
    countChip: {
        ...STYLE.countChip,
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
        color: "primary.main",
    },
});
const COLOR_STYLE = createColorStyles(PRIMARY_COLOR);
const CURRENCY_FORMATTER = new Intl.NumberFormat(CONFIG.currencyLocale, {
    style: "currency",
    currency: CONFIG.currencyCode,
});
const DATE_FORMATTER = new Intl.DateTimeFormat(CONFIG.dateLocale, {
    dateStyle: "medium",
    timeStyle: "short",
});
function isSameId(firstId, secondId) {
    if (firstId === null ||
        firstId === undefined ||
        secondId === null ||
        secondId === undefined) {
        return false;
    }
    return String(firstId) === String(secondId);
}
function getBillingRoles(billingStatus) {
    return {
        isOperator: Boolean(billingStatus?.isOperator),
        isSeller: Boolean(billingStatus?.isSeller),
        isBuyer: Boolean(billingStatus?.isBuyer),
    };
}
function hasBillingParticipation(billingStatus) {
    const roles = getBillingRoles(billingStatus);
    return roles.isOperator || roles.isSeller || roles.isBuyer;
}
function normalizeBillingStatuses(statuses) {
    if (!Array.isArray(statuses)) {
        return [];
    }
    const statusesById = new Map();
    statuses.forEach((billingStatus) => {
        if (billingStatus === null ||
            typeof billingStatus !== "object" ||
            billingStatus.id === null ||
            billingStatus.id === undefined ||
            !hasBillingParticipation(billingStatus)) {
            return;
        }
        statusesById.set(String(billingStatus.id), billingStatus);
    });
    return Array.from(statusesById.values());
}
function getBillableItemTitle(billableItem) {
    const prefix = billableItem.categoryName ?? LABEL.lead;
    return `${prefix}: ${billableItem.id}`;
}
function normalizeTimestamp(timestamp) {
    if (timestamp === null ||
        timestamp === undefined ||
        timestamp === VALUE.empty) {
        return null;
    }
    const numericTimestamp = Number(timestamp);
    if (!Number.isFinite(numericTimestamp)) {
        return null;
    }
    return numericTimestamp < CONFIG.timestampMillisecondsThreshold
        ? numericTimestamp * 1000
        : numericTimestamp;
}
function formatTimestamp(timestamp) {
    const normalizedTimestamp = normalizeTimestamp(timestamp);
    if (normalizedTimestamp === null) {
        return VALUE.fallback;
    }
    const date = new Date(normalizedTimestamp);
    return Number.isNaN(date.getTime())
        ? VALUE.fallback
        : DATE_FORMATTER.format(date);
}
function formatAmount(amount) {
    if (amount === null || amount === undefined || amount === VALUE.empty) {
        return VALUE.fallback;
    }
    const numericAmount = Number(amount);
    return Number.isFinite(numericAmount)
        ? CURRENCY_FORMATTER.format(numericAmount)
        : VALUE.fallback;
}
function buildInvoiceNumber(prefix, invoiceNo) {
    if (invoiceNo === null || invoiceNo === undefined) {
        return VALUE.fallback;
    }
    return `${prefix ?? VALUE.empty}${invoiceNo}`;
}
function getInvoiceNumber(invoice) {
    return buildInvoiceNumber(invoice?.invoiceNoPrefix, invoice?.invoiceNo);
}
function getCancellationInvoiceNumber(invoice) {
    return buildInvoiceNumber(invoice?.cancellationInvoiceNoPrefix, invoice?.invoiceNo);
}
function sanitizeFilename(filename) {
    return String(filename)
        .replace(/[<>:"/\\|?*\u0000-\u001F]/g, "_")
        .trim();
}
function hasCancellationInvoice(invoice) {
    return (invoice?.status === INVOICE_STATUS.cancelled ||
        invoice?.cancellationInvoicePdf != null ||
        invoice?.cancellationInvoiceCreationTimestamp != null);
}
function getInvoiceKey(invoice, index) {
    if (invoice?.id !== null && invoice?.id !== undefined) {
        return `${VALUE.invoiceKeyPrefix}${invoice.id}`;
    }
    return [
        invoice?.type ?? VALUE.unknownInvoiceType,
        getInvoiceNumber(invoice),
        invoice?.invoiceCreationTimestamp ?? index,
    ].join(VALUE.keySeparator);
}
function createInvoiceDocumentRows(invoiceList) {
    return invoiceList
        .flatMap((invoice, index) => {
        const invoiceKey = getInvoiceKey(invoice, index);
        const rows = [
            {
                key: `${invoiceKey}-${VALUE.invoiceKeySuffix}`,
                documentType: DOCUMENT_TYPE.invoice,
                documentLabel: LABEL.invoice,
                invoiceNumber: getInvoiceNumber(invoice),
                creationTimestamp: invoice.invoiceCreationTimestamp,
                amount: invoice.amount,
                pdfData: invoice.invoicePdf,
            },
        ];
        if (hasCancellationInvoice(invoice)) {
            rows.push({
                key: `${invoiceKey}-${VALUE.cancellationKeySuffix}`,
                documentType: DOCUMENT_TYPE.cancellation,
                documentLabel: LABEL.cancellationInvoice,
                invoiceNumber: getCancellationInvoiceNumber(invoice),
                creationTimestamp: invoice.cancellationInvoiceCreationTimestamp,
                amount: invoice.amount,
                pdfData: invoice.cancellationInvoicePdf,
            });
        }
        return rows;
    })
        .sort((firstRow, secondRow) => {
        const firstTimestamp = normalizeTimestamp(firstRow.creationTimestamp) ?? 0;
        const secondTimestamp = normalizeTimestamp(secondRow.creationTimestamp) ?? 0;
        return secondTimestamp - firstTimestamp;
    });
}
function createInvoiceRowModel(documentRow) {
    const filename = `${documentRow.invoiceNumber}${VALUE.pdfExtension}`;
    const downloadLabel = [
        documentRow.documentLabel,
        documentRow.invoiceNumber,
        LABEL.download,
    ].join(" ");
    return {
        ...documentRow,
        formattedTimestamp: formatTimestamp(documentRow.creationTimestamp),
        formattedAmount: formatAmount(documentRow.amount),
        filename,
        downloadLabel,
    };
}
function mergeInvoiceLists(invoiceLists) {
    const invoicesByKey = new Map();
    invoiceLists
        .flatMap((invoiceList) => (Array.isArray(invoiceList) ? invoiceList : []))
        .forEach((invoice, index) => {
        invoicesByKey.set(getInvoiceKey(invoice, index), invoice);
    });
    return Array.from(invoicesByKey.values()).sort((firstInvoice, secondInvoice) => {
        const firstTimestamp = normalizeTimestamp(firstInvoice?.invoiceCreationTimestamp) ?? 0;
        const secondTimestamp = normalizeTimestamp(secondInvoice?.invoiceCreationTimestamp) ?? 0;
        return secondTimestamp - firstTimestamp;
    });
}
function createInvoiceRequests({ accessToken, leadId, roles }) {
    const requests = [];
    if (roles.isSeller) {
        requests.push(fetchAuthorInvoicesAPI(accessToken, leadId));
    }
    if (roles.isBuyer) {
        requests.push(fetchPurchaserInvoicesAPI(accessToken, leadId));
    }
    if (roles.isOperator) {
        requests.push(fetchOperatorInvoicesAPI(accessToken, leadId));
    }
    return requests;
}
function createPdfBlob(pdfData) {
    if (pdfData instanceof Blob) {
        return pdfData;
    }
    if (Array.isArray(pdfData)) {
        return new Blob([Uint8Array.from(pdfData)], { type: VALUE.pdfMimeType });
    }
    if (typeof pdfData !== "string") {
        throw new TypeError(MESSAGE.unknownPdfFormat);
    }
    const separatorIndex = pdfData.indexOf(VALUE.base64Separator);
    const base64Data = separatorIndex >= 0 ? pdfData.substring(separatorIndex + 1) : pdfData;
    const binaryData = window.atob(base64Data);
    const bytes = new Uint8Array(binaryData.length);
    for (let index = 0; index < binaryData.length; index += 1) {
        bytes[index] = binaryData.charCodeAt(index);
    }
    return new Blob([bytes], { type: VALUE.pdfMimeType });
}
function downloadPdf(pdfData, filename) {
    const objectUrl = URL.createObjectURL(createPdfBlob(pdfData));
    const link = document.createElement(VALUE.downloadElement);
    link.href = objectUrl;
    link.download = sanitizeFilename(filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
    }, CONFIG.objectUrlRevokeDelay);
}
function formatDisplayedRows({ from, to, count }) {
    return `${from}–${to} von ${count}`;
}
function createDocumentCountLabel(count) {
    return `${count} ${LABEL.invoiceDocuments}`;
}
function useBillingStatuses({ appAPI, contextAPI, leadId }) {
    const [billingStatuses, setBillingStatuses] = useState(null);
    useEffect(() => {
        if (!leadId || contextAPI.id === null || contextAPI.id === undefined) {
            setBillingStatuses([]);
            return undefined;
        }
        let active = true;
        handleResponse(fetchBillingStatusesAPI(contextAPI.password, contextAPI.id), (statuses) => {
            if (active) {
                setBillingStatuses(normalizeBillingStatuses(statuses));
            }
        }, appAPI.showMessage);
        return () => {
            active = false;
        };
    }, [appAPI.showMessage, contextAPI.id, contextAPI.password, leadId]);
    return billingStatuses;
}
function useInvoiceLists({ appAPI, contextAPI, enabled, leadId, roles }) {
    const [invoiceList, setInvoiceList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        if (!enabled || leadId === null || leadId === undefined) {
            setInvoiceList([]);
            setLoading(false);
            setLoaded(false);
            return undefined;
        }
        const requests = createInvoiceRequests({
            accessToken: contextAPI.password,
            leadId,
            roles,
        });
        if (requests.length === 0) {
            setInvoiceList([]);
            setLoading(false);
            setLoaded(true);
            return undefined;
        }
        let active = true;
        let completedRequestCount = 0;
        const receivedInvoiceLists = new Array(requests.length).fill(null);
        setInvoiceList([]);
        setLoading(true);
        setLoaded(false);
        requests.forEach((request, requestIndex) => {
            const trackedRequest = Promise.resolve(request).finally(() => {
                completedRequestCount += 1;
                if (active && completedRequestCount === requests.length) {
                    setLoading(false);
                    setLoaded(true);
                }
            });
            handleResponse(trackedRequest, (invoices) => {
                if (!active) {
                    return;
                }
                receivedInvoiceLists[requestIndex] = Array.isArray(invoices)
                    ? invoices
                    : [];
                setInvoiceList(mergeInvoiceLists(receivedInvoiceLists));
            }, appAPI.showMessage);
        });
        return () => {
            active = false;
        };
    }, [
        appAPI.showMessage,
        contextAPI.password,
        enabled,
        leadId,
        roles.isBuyer,
        roles.isOperator,
        roles.isSeller,
    ]);
    const productInvoices = useMemo(() => invoiceList.filter((invoice) => invoice.type === INVOICE_TYPE.product), [invoiceList]);
    const transactionInvoices = useMemo(() => invoiceList.filter((invoice) => invoice.type === INVOICE_TYPE.transaction), [invoiceList]);
    return {
        loaded,
        loading,
        productInvoices,
        transactionInvoices,
    };
}
function useInvoiceDownload({ filename, pdfData, showMessage }) {
    return useCallback(() => {
        try {
            downloadPdf(pdfData, filename);
        }
        catch (error) {
            showMessage?.(error?.message ?? MESSAGE.downloadFailed);
        }
    }, [filename, pdfData, showMessage]);
}
function useInvoiceTableModel(invoiceList) {
    const [page, setPage] = useState(CONFIG.defaultPage);
    const [rowsPerPage, setRowsPerPage] = useState(CONFIG.defaultRowsPerPage);
    const documentRows = useMemo(() => createInvoiceDocumentRows(invoiceList), [invoiceList]);
    const visibleRows = useMemo(() => {
        const startIndex = page * rowsPerPage;
        return documentRows
            .slice(startIndex, startIndex + rowsPerPage)
            .map(createInvoiceRowModel);
    }, [documentRows, page, rowsPerPage]);
    const showPagination = documentRows.length > CONFIG.rowsPerPageOptions[0];
    useEffect(() => {
        const lastPage = Math.max(CONFIG.defaultPage, Math.ceil(documentRows.length / rowsPerPage) - 1);
        if (page > lastPage) {
            setPage(lastPage);
        }
    }, [documentRows.length, page, rowsPerPage]);
    const handlePageChange = useCallback((_event, nextPage) => {
        setPage(nextPage);
    }, []);
    const handleRowsPerPageChange = useCallback((event) => {
        setRowsPerPage(Number.parseInt(event.target.value, CONFIG.integerRadix));
        setPage(CONFIG.defaultPage);
    }, []);
    return {
        count: documentRows.length,
        countLabel: createDocumentCountLabel(documentRows.length),
        empty: documentRows.length === 0,
        handlePageChange,
        handleRowsPerPageChange,
        page,
        rowsPerPage,
        showPagination,
        visibleRows,
    };
}
function createInvoiceSections({ productInvoices, showProductInvoices, showTransactionInvoices, transactionInvoices, }) {
    return [
        {
            key: INVOICE_TYPE.transaction,
            visible: showTransactionInvoices,
            title: LABEL.transactionInvoices,
            invoiceList: transactionInvoices,
        },
        {
            key: INVOICE_TYPE.product,
            visible: showProductInvoices,
            title: LABEL.productInvoices,
            invoiceList: productInvoices,
        },
    ];
}
function useInvoicingController({ appAPI, billableItem, contextAPI }) {
    const roles = useMemo(() => getBillingRoles(billableItem), [billableItem]);
    const invoices = useInvoiceLists({
        appAPI,
        contextAPI,
        enabled: hasBillingParticipation(billableItem),
        leadId: billableItem.id,
        roles,
    });
    const sections = useMemo(() => createInvoiceSections({
        productInvoices: invoices.productInvoices,
        showProductInvoices: roles.isBuyer || roles.isSeller,
        showTransactionInvoices: roles.isOperator || roles.isSeller,
        transactionInvoices: invoices.transactionInvoices,
    }), [
        invoices.productInvoices,
        invoices.transactionInvoices,
        roles.isBuyer,
        roles.isOperator,
        roles.isSeller,
    ]);
    return {
        canOpenLead: Boolean(billableItem.available),
        leadTitle: getBillableItemTitle(billableItem),
        leadUrl: `${VALUE.leadRoutePrefix}${billableItem.id}`,
        loading: invoices.loading,
        sections,
        showMessage: appAPI.showMessage,
    };
}
function useInvoicingDetailsController({ appAPI, contextAPI, lead, showTransactionInvoices, }) {
    const billingStatuses = useBillingStatuses({
        appAPI,
        contextAPI,
        leadId: lead?.id,
    });
    const billingStatus = useMemo(() => {
        if (!Array.isArray(billingStatuses)) {
            return null;
        }
        return (billingStatuses.find((status) => isSameId(status.id, lead?.id)) ?? null);
    }, [billingStatuses, lead?.id]);
    const roles = useMemo(() => getBillingRoles(billingStatus), [billingStatus]);
    const billingStatusLoading = billingStatuses === null;
    const showTransactions = Boolean(billingStatus) &&
        Boolean(showTransactionInvoices) &&
        (roles.isOperator || roles.isSeller);
    const showProducts = Boolean(billingStatus) && (roles.isBuyer || roles.isSeller);
    // If transaction invoices are hidden in this view, an operator-only request
    // is unnecessary. Seller requests remain enabled because the author endpoint
    // can also contain product invoices.
    const effectiveRoles = useMemo(() => ({
        isOperator: roles.isOperator && showTransactions,
        isSeller: roles.isSeller,
        isBuyer: roles.isBuyer,
    }), [roles.isBuyer, roles.isOperator, roles.isSeller, showTransactions]);
    const invoices = useInvoiceLists({
        appAPI,
        contextAPI,
        enabled: !billingStatusLoading &&
            Boolean(billingStatus) &&
            (showTransactions || showProducts),
        leadId: lead?.id,
        roles: effectiveRoles,
    });
    const sections = useMemo(() => createInvoiceSections({
        productInvoices: invoices.productInvoices,
        showProductInvoices: showProducts,
        showTransactionInvoices: showTransactions,
        transactionInvoices: invoices.transactionInvoices,
    }), [
        invoices.productInvoices,
        invoices.transactionInvoices,
        showProducts,
        showTransactions,
    ]);
    return {
        loading: billingStatusLoading || invoices.loading,
        sections,
        showMessage: appAPI.showMessage,
    };
}
function BillingStatusNotFound() {
    return (<Card variant={UI.variant.outlined} sx={STYLE.statusCard}>

      <CardContent sx={STYLE.statusContent}>

        <ReceiptLongOutlinedIcon sx={STYLE.statusIcon}/>

        <Typography variant={UI.variant.h6} sx={STYLE.statusTitle}>

          {LABEL.noBillingData}

        </Typography>

        <Typography variant={UI.variant.body2} color={UI.color.secondaryText} sx={STYLE.statusDescription}>

          {LABEL.noBillingStatus}

        </Typography>

      </CardContent>

    </Card>);
}
function InvoicingHeader({ model }) {
    return (<Box sx={COLOR_STYLE.invoicingHeader}>

      <Box sx={STYLE.headerTitleContainer}>

        <Typography variant={UI.variant.overline} sx={STYLE.headerEyebrow}>

          {LABEL.invoices}

        </Typography>

        <Typography variant={UI.variant.h6} noWrap={UI.boolean.true} sx={STYLE.headerTitle}>

          {model.leadTitle}

        </Typography>

      </Box>

      {model.canOpenLead && (<Tooltip title={LABEL.openLead}>

          <IconButton component={NavLink} to={model.leadUrl} aria-label={LABEL.openLead} sx={STYLE.headerAction}>

            <OpenInNewIcon fontSize={UI.size.small}/>

          </IconButton>

        </Tooltip>)}

    </Box>);
}
function InvoiceDocumentChip({ model }) {
    const color = model.documentType === DOCUMENT_TYPE.cancellation
        ? UI.color.warning
        : UI.color.default;
    return (<Chip size={UI.size.small} label={model.documentLabel} variant={UI.variant.outlined} color={color} sx={STYLE.documentChip}/>);
}
function InvoiceDownloadButton({ model, showMessage }) {
    const handleDownload = useInvoiceDownload({
        filename: model.filename,
        pdfData: model.pdfData,
        showMessage,
    });
    return (<Tooltip title={model.downloadLabel}>

      <Box component={UI.component.span}>

        <IconButton size={UI.size.small} aria-label={model.downloadLabel} disabled={!model.pdfData} onClick={handleDownload} sx={STYLE.downloadButton}>

          <DownloadIcon fontSize={UI.size.small}/>

        </IconButton>

      </Box>

    </Tooltip>);
}
function InvoiceTableHead() {
    return (<TableHead>

      <TableRow sx={STYLE.tableHeadRow}>

        <TableCell>{LABEL.date}</TableCell>

        <TableCell>{LABEL.document}</TableCell>

        <TableCell>{LABEL.invoiceNumber}</TableCell>

        <TableCell align={UI.align.right}>{LABEL.amount}</TableCell>

        <TableCell align={UI.align.center}>{LABEL.actions}</TableCell>

      </TableRow>

    </TableHead>);
}
function InvoiceTableRow({ model, showMessage, showPagination }) {
    const rowStyle = showPagination ? STYLE.paginatedTableRow : STYLE.tableRow;
    return (<TableRow hover={UI.boolean.true} sx={rowStyle}>

      <TableCell sx={STYLE.dateCell}>{model.formattedTimestamp}</TableCell>

      <TableCell sx={STYLE.documentCell}>

        <InvoiceDocumentChip model={model}/>

      </TableCell>

      <TableCell sx={STYLE.numberCell}>{model.invoiceNumber}</TableCell>

      <TableCell align={UI.align.right} sx={STYLE.numberCell}>

        {model.formattedAmount}

      </TableCell>

      <TableCell align={UI.align.center} sx={STYLE.actionCell}>

        <InvoiceDownloadButton model={model} showMessage={showMessage}/>

      </TableCell>

    </TableRow>);
}
function InvoiceRows({ model, showMessage }) {
    return model.visibleRows.map((row) => (<InvoiceTableRow key={row.key} model={row} showMessage={showMessage} showPagination={model.showPagination}/>));
}
function InvoiceTableView({ model, showMessage, title }) {
    return (<TableContainer sx={STYLE.tableContainer}>

      <Table size={UI.size.small} aria-label={title} sx={STYLE.table}>

        <InvoiceTableHead />

        <TableBody>

          <InvoiceRows model={model} showMessage={showMessage}/>

        </TableBody>

      </Table>

    </TableContainer>);
}
function InvoicePagination({ model }) {
    if (!model.showPagination) {
        return null;
    }
    return (<TablePagination component={UI.component.div} count={model.count} page={model.page} rowsPerPage={model.rowsPerPage} rowsPerPageOptions={CONFIG.rowsPerPageOptions} onPageChange={model.handlePageChange} onRowsPerPageChange={model.handleRowsPerPageChange} labelRowsPerPage={LABEL.rowsPerPage} labelDisplayedRows={formatDisplayedRows} sx={STYLE.pagination}/>);
}
function InvoiceEmptyState() {
    return (<Box sx={STYLE.emptyState}>

      <ReceiptLongOutlinedIcon sx={STYLE.emptyStateIcon}/>

      <Typography variant={UI.variant.body1} sx={STYLE.emptyStateTitle}>

        {LABEL.noInvoices}

      </Typography>

      <Typography variant={UI.variant.body2} sx={STYLE.emptyStateDescription}>

        {LABEL.noInvoicesForArea}

      </Typography>

    </Box>);
}
function InvoiceCardTitle({ count, countLabel, title }) {
    return (<Stack direction={UI.direction.row} alignItems={UI.align.center} spacing={UI.stack.titleSpacing} sx={STYLE.cardTitleStack}>

      <Typography variant={UI.variant.h6} noWrap={UI.boolean.true} sx={STYLE.cardTitle}>

        {title}

      </Typography>

      <Chip label={count} size={UI.size.small} aria-label={countLabel} sx={COLOR_STYLE.countChip}/>

    </Stack>);
}
function InvoiceTableCard({ invoiceList, showMessage, title }) {
    const model = useInvoiceTableModel(invoiceList);
    return (<Card variant={UI.variant.outlined} sx={STYLE.invoiceCard}>

      <CardHeader title={<InvoiceCardTitle count={model.count} countLabel={model.countLabel} title={title}/>} sx={STYLE.cardHeader}/>

      <CardContent sx={STYLE.cardContent}>

        {model.empty ? (<InvoiceEmptyState />) : (<>

            <InvoiceTableView model={model} showMessage={showMessage} title={title}/>

            <InvoicePagination model={model}/>

          </>)}

      </CardContent>

    </Card>);
}
function InvoiceSection({ model, showMessage }) {
    if (!model.visible) {
        return null;
    }
    return (<Grid item={UI.boolean.true} xs={UI.grid.full}>

      <InvoiceTableCard invoiceList={model.invoiceList} showMessage={showMessage} title={model.title}/>

    </Grid>);
}
function InvoiceSections({ sections, showMessage }) {
    return (<Grid container={UI.boolean.true} spacing={UI.grid.sectionSpacing} alignItems={UI.align.start}>

      {sections.map((section) => (<InvoiceSection key={section.key} model={section} showMessage={showMessage}/>))}

    </Grid>);
}
function InvoicingView({ model }) {
    return (<Card variant={UI.variant.outlined} sx={STYLE.invoicingCard}>

      <InvoicingHeader model={model}/>

      {model.loading && <LinearProgress />}

      <Box sx={STYLE.invoicingContent}>

        <InvoiceSections sections={model.sections} showMessage={model.showMessage}/>

      </Box>

    </Card>);
}
export function InvoicingWrapper({ appAPI, contextAPI }) {
    const { leadId } = useParams();
    const billingStatuses = useBillingStatuses({
        appAPI,
        contextAPI,
        leadId,
    });
    if (billingStatuses === null) {
        return null;
    }
    const billingStatus = billingStatuses.find((status) => isSameId(status.id, leadId));
    return (<Box sx={STYLE.wrapper}>

      {billingStatus ? (<Invoicing appAPI={appAPI} contextAPI={contextAPI} billableItem={billingStatus}/>) : (<BillingStatusNotFound />)}

    </Box>);
}
export function Invoicing({ appAPI, contextAPI, billableItem }) {
    const model = useInvoicingController({
        appAPI,
        billableItem,
        contextAPI,
    });
    return <InvoicingView model={model}/>;
}
export function InvoicingDetails({ appAPI, contextAPI, lead, showTransactionInvoices, }) {
    const model = useInvoicingDetailsController({
        appAPI,
        contextAPI,
        lead,
        showTransactionInvoices,
    });
    return (<>

      {model.loading && <LinearProgress sx={STYLE.detailsProgress}/>}

      <InvoiceSections sections={model.sections} showMessage={model.showMessage}/>

    </>);
}
